import { parseDatenFile } from '@emdzej/ncsx-daten';
const padCi = (ci) => ci.toString(16).toUpperCase().padStart(2, '0');
const CXX_RE = /^(.+)\.C([0-9A-Fa-f]{2})$/;
export class CabdNotFoundError extends Error {
    constructor(message) {
        super(message);
        this.name = 'CabdNotFoundError';
    }
}
/**
 * Resolve and lazily load a CABD `<basename>.Cxx` file from the chassis directory.
 * Caches the parsed `DatenFile` per `(basename, ci)`. Caches the chassis directory
 * listing once on first probe so a typical "scan all modules up front" sweep makes a
 * single `source.list()` round-trip.
 *
 * **Module resolution is by on-disk file basename, not by SGFAM.** Pick a module by its
 * basename (`KMB_E46`, `EWS`, `LSZ_E46`, …) plus a coding index (`0x07` for `.C07`). For
 * FA-driven flows, the basename comes from `SGAUSWAHL.SGNAME` and the coding index from
 * the leading-`C`-stripped value of `SGAUSWAHL.CBD`. See
 * [`docs/ecu-selection.md` §8](../../../docs/ecu-selection.md) for the lookup contract.
 *
 * `SGFAM.CABD` is **not** a file basename and there is no string transform from it to one
 * — see the doc for the antipatterns to avoid.
 */
export class CabdLoader {
    source;
    chassisDir;
    cache = new Map();
    listingPromise = null;
    modulesPromise = null;
    constructor(source, 
    /** Path of the chassis directory under the source root (e.g. `e46`). */
    chassisDir) {
        this.source = source;
        this.chassisDir = chassisDir;
    }
    /**
     * Enumerate every `.C??` file in the chassis directory, grouped by basename. Sorted
     * alphabetically by `moduleName`. Same logic NCS Dummy's `ModuleListReader` uses to
     * populate its Modules dropdown.
     *
     * The result is cached — call it as often as you like.
     */
    async listModules() {
        if (!this.modulesPromise) {
            this.modulesPromise = this.computeModules();
        }
        return this.modulesPromise;
    }
    /**
     * Open one specific module by on-disk basename + coding index. Throws
     * `CabdNotFoundError` if `<basename>.C<ci>` isn't present (case-insensitive).
     */
    async openModule(moduleName, codingIndex) {
        const cacheKey = `${moduleName}.C${padCi(codingIndex)}`;
        const cached = this.cache.get(cacheKey);
        if (cached)
            return cached;
        const entries = await this.listing();
        const want = cacheKey.toLowerCase();
        const filename = entries.find((e) => e.toLowerCase() === want);
        if (!filename) {
            throw new CabdNotFoundError(`${cacheKey} not in ${this.chassisDir} — listModules() to see what's available`);
        }
        const bytes = await this.source.read(`${this.chassisDir}/${filename}`);
        const file = parseDatenFile(bytes);
        this.cache.set(cacheKey, file);
        return file;
    }
    async computeModules() {
        const entries = await this.listing();
        const byBase = new Map();
        for (const entry of entries) {
            const match = CXX_RE.exec(entry);
            if (!match)
                continue;
            const base = match[1];
            const ci = Number.parseInt(match[2], 16);
            if (!Number.isFinite(ci))
                continue;
            let set = byBase.get(base);
            if (!set) {
                set = new Set();
                byBase.set(base, set);
            }
            set.add(ci);
        }
        const out = [];
        for (const [base, cis] of byBase) {
            out.push({ moduleName: base, codingIndexes: [...cis].sort((a, b) => a - b) });
        }
        out.sort((a, b) => a.moduleName.localeCompare(b.moduleName));
        return out;
    }
    listing() {
        if (!this.listingPromise) {
            this.listingPromise = this.source.list(this.chassisDir);
        }
        return this.listingPromise;
    }
}
//# sourceMappingURL=cabd-loader.js.map